import React from 'react';
import { Card } from '../ui/Card';
import { LucideIcon, Table } from 'lucide-react';
import { useTheme } from '@mui/material/styles';

interface BasicCardProps {
    title: string;
    description: string;
    color?: string;
    palette?: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error';
    onClick: () => void;
    icon?: LucideIcon;
    square?: boolean;
    filled?: boolean;
    size?: 'sm' | 'md';
}

export const BasicCard: React.FC<BasicCardProps> = ({
    title,
    description,
    color,
    palette,
    onClick,
    icon: Icon = Table,
    square = false,
    filled = false,
    size = 'md'
}) => {
    const theme = useTheme();
    const paletteColor = palette ? (theme.palette as any)[palette]?.main : undefined;
    const effectiveColor = color || paletteColor || theme.palette.primary.main;
    const compact = size === 'sm';
    const iconPaddingClass = compact ? 'p-1.5' : 'p-3';
    const iconSize = compact ? 18 : 24;
    const gapClass = compact ? 'gap-2.5' : 'gap-4';
    const titleClass = compact
        ? `${filled ? 'text-white' : 'text-slate-900 dark:text-white'} text-[12px] font-semibold leading-snug line-clamp-1 break-words`
        : `${filled ? 'text-white' : 'text-slate-900 dark:text-white'} font-bold line-clamp-2`;
    const descClass = compact
        ? `${filled ? 'text-white/90' : 'text-slate-500 dark:text-slate-400'} text-[10px] leading-tight line-clamp-1 break-words`
        : `${filled ? 'text-white/90' : 'text-slate-500 dark:text-slate-400'} text-sm line-clamp-1`;
    return (
        <Card
            className={`cursor-pointer transition-all hover:scale-105 active:scale-95 ${filled ? '' : 'border-l-4'}`}
            style={{
                borderLeftColor: filled ? undefined : effectiveColor,
                backgroundColor: filled ? effectiveColor : undefined,
                aspectRatio: square ? '1 / 1' as any : undefined
            }}
            onClick={onClick}
            compact={compact}
        >
            <div className={`flex items-center ${gapClass} h-full`}>
                <div
                    className={`${iconPaddingClass} rounded-lg`}
                    style={{
                        backgroundColor: filled ? 'rgba(255,255,255,0.2)' : effectiveColor,
                        color: '#fff'
                    }}
                >
                    <Icon size={iconSize} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className={titleClass} title={title}>{title}</h3>
                    <p className={descClass} title={description}>{description}</p>
                </div>
            </div>
        </Card>
    );
};
